import {
  API_SCOPES,
  API_SCOPE_LABEL,
  WEBHOOK_EVENTS,
  WEBHOOK_EVENT_LABEL,
  type ApiKey,
  type ApiScope,
  type Webhook,
  type WebhookDelivery,
  type WebhookEvent,
} from '@cyberscore/contracts';
import { Copy, KeyRound, Loader2, Plus, RefreshCw, Send, Webhook as WebhookIcon } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { when } from '../clients/parts';
import { useApiKeyChanges, useApiKeys, useDeliveries, useWebhookChanges, useWebhooks } from './api';

const message = (e: unknown, fallback: string) =>
  e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : fallback;
const API_BASE = `${window.location.origin}/api/v1`;

/**
 * API keys and webhooks (task 71): how the insurer's own systems read the book and hear about changes.
 * Tenant admins only. Keys and webhook secrets are shown once.
 * `embedded`: inside Tenant 360's tab (task 102), under a smaller heading.
 */
export function IntegrationsPage({ embedded = false }: { embedded?: boolean } = {}) {
  return (
    <>
      <div className={`page-head${embedded ? ' embedded' : ''}`}>
        {embedded ? <h2>API and webhooks</h2> : <h1>API and webhooks</h1>}
        <p>Connect your policy or claims system: read clients, scores, decisions and alerts with an API key, and hear about changes as they happen with webhooks.</p>
      </div>
      <div className="stack">
        <KeysCard />
        <WebhooksCard />
      </div>
    </>
  ); // prettier-ignore
}

/** A key or secret, shown once, with a copy button. */
function SecretOnce({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  const id = `secret-${label.replace(/\W+/g, '-').toLowerCase()}`;
  const copy = () =>
    navigator.clipboard
      .writeText(value)
      .then(() => toast(`${label} copied`))
      .catch(() => {
        (document.getElementById(id) as HTMLInputElement | null)?.select();
        toast(`Select the ${label.toLowerCase()} and copy it`, 'bad');
      });
  return (
    <div className="secret-once" role="status">
      <label htmlFor={id}>{label}: copy it now. It won’t be shown again.</label>
      <div className="row gap">
        <input id={id} className="input mono" readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="btn" onClick={copy}><Copy size={15} /> Copy</button>
      </div>
    </div>
  ); // prettier-ignore
}

// —— API keys ——

function KeysCard() {
  const keys = useApiKeys();
  const { revoke } = useApiKeyChanges();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const state = (k: ApiKey) => k.revoked_at ? <span className="pill neutral">Revoked</span> : k.expires_at && new Date(k.expires_at) < new Date() ? <span className="pill warn">Expired</span> : <span className="pill ok">Active</span>; // prettier-ignore
  return (
    <section className="card" aria-labelledby="keys-title">
      <div className="dv-bar">
        <h2 id="keys-title" className="section-title grow"><KeyRound size={16} aria-hidden /> API keys</h2>
        <button type="button" className="btn btn-primary" onClick={() => { setToken(null); setCreating(true); }}><Plus size={15} /> New key</button>
      </div>
      <div className="pad-x">
        {token && <SecretOnce label="API key" value={token} />}
        <p className="muted small">A key reads what an auditor can see, only through the scopes you give it, and changes nothing. Send it as <code>Authorization: Bearer csk_…</code> to <code>{API_BASE}</code>: <code>/clients</code>, <code>/clients/:key</code>, <code>/assessments/:id/score</code>, <code>/decisions?since=</code>, <code>/alerts?since=</code>.</p>
      </div>
      {keys.isPending ? <div className="empty">Loading…</div> : !keys.data?.length ? (
        <div className="empty"><KeyRound size={28} /><strong>No API keys yet</strong>Make one for each system that reads from CyberScore, so you can revoke one without the others.</div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Key</th><th className="wide">Can read</th><th className="wide">Last used</th><th className="wide">Expires</th><th>State</th><th /></tr></thead>
            <tbody>
              {keys.data.map((k) => (
                <tr key={k.id}>
                  <td><b>{k.name}</b><div className="muted small mono">csk_{k.prefix}_…</div></td>
                  <td className="wide small">{k.scopes.map((s) => API_SCOPE_LABEL[s]).join(', ')}</td>
                  <td className="wide small">{k.last_used_at ? when(k.last_used_at) : <span className="muted">Never</span>}</td>
                  <td className="wide small">{k.expires_at ? when(k.expires_at) : <span className="muted">Never</span>}</td>
                  <td>{state(k)}</td>
                  <td className="right">{!k.revoked_at && <button type="button" className="btn btn-sm" disabled={revoke.isPending} onClick={async () => { if (!window.confirm(`Revoke “${k.name}”? Anything using it stops working at once.`)) return; try { await revoke.mutateAsync(k.id); toast(`${k.name} revoked.`); } catch (e) { toast(message(e, 'Could not revoke'), 'bad'); } }}>Revoke</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <NewKey onClose={() => setCreating(false)} onCreated={(t) => { setCreating(false); setToken(t); }} />}
    </section>
  ); // prettier-ignore
}

function NewKey({ onClose, onCreated }: { onClose: () => void; onCreated: (token: string) => void }) {
  const { create } = useApiKeyChanges();
  const [name, setName] = useState('Policy system');
  const [scopes, setScopes] = useState<ApiScope[]>(['clients:read', 'scores:read']);
  const [days, setDays] = useState('365');
  const [banner, setBanner] = useState<string | null>(null);
  const save = async () => {
    setBanner(null);
    try {
      const k = await create.mutateAsync({
        name: name.trim(),
        scopes,
        expires_in_days: days ? Number(days) : null,
      });
      onCreated(k.token);
    } catch (e) {
      setBanner(message(e, 'Could not make the key'));
    }
  };
  return (
    <Drawer
      title="New API key"
      subtitle="It reads as an auditor would, through these scopes only."
      onClose={onClose}
      footer={<><span className="grow" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={create.isPending || !name.trim() || !scopes.length} onClick={save}>{create.isPending && <Loader2 size={15} className="spin" />}Make key</button></>} // prettier-ignore
    >
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      <div className="field"><label htmlFor="key-name">Name</label><input id="key-name" className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /><span className="hint">The system that will use it.</span></div>
      <fieldset className="field checks"><legend>Can read</legend>
        {API_SCOPES.map((s) => <label key={s} className="toggle-row small"><input type="checkbox" checked={scopes.includes(s)} onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} /> <span>{API_SCOPE_LABEL[s]}</span></label>)}
      </fieldset>
      <div className="field"><label htmlFor="key-days">Stops working</label>
        <select id="key-days" className="input" value={days} onChange={(e) => setDays(e.target.value)}>
          <option value="90">In 90 days</option><option value="365">In a year</option><option value="730">In two years</option><option value="">Only when revoked</option>
        </select>
      </div>
    </Drawer>
  ); // prettier-ignore
}

// —— webhooks ——

function WebhooksCard() {
  const hooks = useWebhooks();
  const [open, setOpen] = useState<Webhook | 'new' | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  return (
    <section className="card" aria-labelledby="hooks-title">
      <div className="dv-bar">
        <h2 id="hooks-title" className="section-title grow"><WebhookIcon size={16} aria-hidden /> Webhooks</h2>
        <button type="button" className="btn btn-primary" onClick={() => { setSecret(null); setOpen('new'); }}><Plus size={15} /> New webhook</button>
      </div>
      <div className="pad-x">
        {secret && <SecretOnce label="Signing secret" value={secret} />}
        <p className="muted small">Each delivery is a JSON POST signed in the <code>CyberScore-Signature</code> header: <code>t=&lt;unix time&gt;,v1=&lt;HMAC-SHA256 of “t.body” with the secret, in hex&gt;</code>. Answer with a 2xx within 10 seconds; anything else is tried again after 1, 5 and 30 minutes, 2 and 12 hours and a day.</p>
      </div>
      {hooks.isPending ? <div className="empty">Loading…</div> : !hooks.data?.length ? (
        <div className="empty"><WebhookIcon size={28} /><strong>No webhooks yet</strong>Tell your system when a decision is made, a score changes or an alert is raised.</div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Address</th><th className="wide">Events</th><th className="right">Last 7 days</th><th>State</th></tr></thead>
            <tbody>
              {hooks.data.map((h) => (
                <tr key={h.id} className="clickable" onClick={() => { setSecret(null); setOpen(h); }}>
                  <td><button type="button" className="linklike cell-title mono" onClick={() => { setSecret(null); setOpen(h); }}>{h.url}</button>{h.description && <div className="muted small">{h.description}</div>}</td>
                  <td className="wide small">{h.events.length} event{h.events.length === 1 ? '' : 's'}</td>
                  <td className="right small">{h.recent.delivered} delivered{h.recent.failing ? <div className="warn-text">{h.recent.failing} retrying</div> : null}{h.recent.failed ? <div className="bad-text">{h.recent.failed} failed</div> : null}</td>
                  <td>{h.active ? <span className="pill ok">On</span> : <span className="pill neutral">Off</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <WebhookDrawer hook={open === 'new' ? null : open} onClose={() => setOpen(null)} onSecret={(s) => { setSecret(s); setOpen(null); }} />}
    </section>
  ); // prettier-ignore
}

const DELIVERY_TONE: Record<WebhookDelivery['status'], string> = {
  pending: 'warn',
  delivered: 'ok',
  failed: 'bad',
};

function WebhookDrawer({
  hook,
  onClose,
  onSecret,
}: {
  hook: Webhook | null;
  onClose: () => void;
  onSecret: (s: string) => void;
}) {
  // prettier-ignore
  const toast = useToast();
  const { create, update, rotate, test, retry } = useWebhookChanges();
  const deliveries = useDeliveries(hook?.id ?? null);
  const [url, setUrl] = useState(hook?.url ?? 'https://');
  const [description, setDescription] = useState(hook?.description ?? '');
  const [events, setEvents] = useState<WebhookEvent[]>(
    hook?.events ?? ['assessment.decided', 'alert.raised'],
  );
  const [active, setActive] = useState(hook?.active ?? true);
  const [banner, setBanner] = useState<string | null>(null);
  const busy = create.isPending || update.isPending;

  const save = async () => {
    setBanner(null);
    try {
      const body = { url: url.trim(), description: description.trim(), events, active };
      if (hook) {
        await update.mutateAsync({ id: hook.id, revision: hook.revision, ...body });
        toast('Webhook saved.');
        onClose();
      } else onSecret((await create.mutateAsync(body)).secret);
    } catch (e) {
      setBanner(message(e, 'Could not save the webhook'));
    }
  };
  const sendTest = async () => {
    if (!hook) return;
    try {
      const d = await test.mutateAsync(hook.id);
      toast(d.status === 'delivered' ? `Test delivered (HTTP ${d.last_code}).` : `Test not taken: ${d.last_error ?? 'no answer'}.`, d.status === 'delivered' ? undefined : 'bad'); // prettier-ignore
    } catch (e) {
      toast(message(e, 'Could not send the test'), 'bad');
    }
  };

  return (
    <Drawer
      title={hook ? 'Webhook' : 'New webhook'}
      subtitle={hook ? hook.url : 'Where to send events, and which ones.'}
      onClose={onClose}
      footer={<>{hook && <><button type="button" className="btn" disabled={test.isPending} onClick={sendTest}>{test.isPending ? <Loader2 size={15} className="spin" /> : <Send size={15} />} Send a test</button><button type="button" className="btn btn-ghost" disabled={rotate.isPending} onClick={async () => { if (!window.confirm('Make a new signing secret? The current one stops working at once.')) return; onSecret((await rotate.mutateAsync(hook.id)).secret); }}><RefreshCw size={15} /> New secret</button></>}<span className="grow" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={busy || !events.length} onClick={save}>{busy && <Loader2 size={15} className="spin" />}{hook ? 'Save' : 'Add webhook'}</button></>} // prettier-ignore
    >
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      <div className="field"><label htmlFor="wh-url">Address</label><input id="wh-url" className="input mono" type="url" maxLength={500} value={url} onChange={(e) => setUrl(e.target.value)} /><span className="hint">An https address on the internet.</span></div>
      <div className="field"><label htmlFor="wh-desc">Note</label><input id="wh-desc" className="input" maxLength={200} placeholder="Which system, who looks after it" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
      <fieldset className="field checks"><legend>Events</legend>
        {WEBHOOK_EVENTS.map((ev) => <label key={ev} className="toggle-row small"><input type="checkbox" checked={events.includes(ev)} onChange={(e) => setEvents(e.target.checked ? [...events, ev] : events.filter((x) => x !== ev))} /> <span>{WEBHOOK_EVENT_LABEL[ev]} <code className="muted">{ev}</code></span></label>)}
      </fieldset>
      <label className="toggle-row small"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> <span>On</span></label>
      {hook && (
        <>
          <div className="eyebrow">Recent deliveries</div>
          {!deliveries.data?.length ? <p className="muted small">Nothing sent yet. Send a test to check your end.</p> : (
            <ul className="plain-list small deliveries" aria-label="Deliveries">
              {deliveries.data.map((d) => (
                <li key={d.id}>
                  <span className={`pill ${DELIVERY_TONE[d.status]}`}>{d.status === 'pending' && d.attempts ? 'Retrying' : d.status === 'pending' ? 'Queued' : d.status === 'delivered' ? 'Delivered' : 'Failed'}</span>{' '}
                  <code>{d.type}</code> <span className="muted">{when(d.created_at)}{d.attempts > 1 ? ` · ${d.attempts} tries` : ''}{d.last_error ? ` · ${d.last_error}` : ''}</span>
                  {d.status === 'failed' && <button type="button" className="linklike small" onClick={() => retry.mutate({ id: hook.id, delivery: d.id })}>Try again</button>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Drawer>
  ); // prettier-ignore
}
